/**
 * wild_models — the wild creatures' bodies, bent in the vertex shader (the physics follow-up's item 3, 2026-10-05).
 *
 * The six hunted species (WildsModule) are single-mesh Meshy GLBs with NO rig — like the herds (lib/ambient_models.ts),
 * so the shader bends them: each body is normalised to THE FRAME (head +Z, feet at y = 0, the longer of x / z = 1 —
 * WildsModule's SPECIES_LEN convention; THE WRAP LAW: normalizeCreature float-copies the int16 positions first), its rig
 * is MEASURED from its own vertices, and a model-space delta is added in a positionNode — measured in
 * `positionGeometry`, added to `positionLocal`, with `positionPrevious` assigned the same delta at the previous phase
 * (the positionPrevious LAW — motion vectors).
 *
 * ONE MATERIAL PER SPECIES, ONE STATE PER BEAST. Wild creatures are individual meshes, not a batch, so the per-beast
 * numbers ride two vec4 uniforms refreshed PER DRAW from `mesh.userData.wildA / wildB` (`onObjectUpdate` — the
 * object-group uniforms three re-uploads for every render object, shadows included): never a shared uniform that
 * would animate every beast of a species in step. A dissolve clone (WildsModule.beginDissolve) copies the positionNode
 * and so the same uniforms — a dying beast keeps its last pose.
 *
 *   wildA = (phase, phasePrev, neck, swing)        — lib/wild_gait.ts
 *   wildB = (windup, lunge, run, 0)
 *
 * The bodies (looked at — render-glb-views of the served LOD0s):
 *  quadruped (ridge stalker, duneback brute, mire lurker): the herds' lateral-sequence walk and neck (deltaFor), plus a
 *    tail that sways with the gait — a travelling wag down everything behind the hind legs' reach.
 *  serpent (crag wyrm — a COILED serpent with its neck raised): the coils ripple — three waves travel round the coil
 *    about its measured centre — and a lateral wave travels UP the neck; the windup rears the head back, the strike
 *    throws it forward.
 *  drifter (spore drone — a jellyfish cap over hanging tentacles): it hovers, bobs, the cap pulses (squash and spread),
 *    every tentacle sways on its own phase and the bundle trails behind as it speeds up.
 *  strider (glass shrike — an upright raptor, wings folded): two legs alternate about the measured hip, the stepping
 *    foot lifts, the body bobs twice a stride, the head thrusts (a pigeon's bob), the wings flare on the windup and
 *    beat at a run (rotated about the measured shoulder).
 */
import * as THREE from 'three/webgpu';
import { Fn, uniform, positionLocal, positionGeometry, positionPrevious, sin, cos, abs, max, float, vec2, vec3, vec4, step, smoothstep, sign, clamp, length, atan } from 'three/tsl';
import { normalizeCreature, quadrupedRig, deltaFor, type QuadrupedRig } from './ambient_models.js';
import { wildGaitFor, type WildBody } from './wild_gait.js';

export interface WildQuadRig extends QuadrupedRig {
  /** Everything behind this z (the hind legs' reach) is tail; the tail ends at `tailEnd` (the body's rearmost z). */
  tailZ: number;
  tailEnd: number;
}
export interface SerpentRig {
  /** Below this height the body is coil; above it, the raised neck. */
  coilTop: number;
  /** The coil's centre (x, z) — the ripple travels round it. */
  cx: number;
  cz: number;
  height: number;
}
export interface DrifterRig {
  /** Above this height is the cap; below it, the tentacles. */
  capBottom: number;
  capMid: number;
  cx: number;
  cz: number;
  height: number;
}
export interface StriderRig {
  /** The legs: below `legTop`, within `legX` of the midline, ahead of the tail; the hip pivot at (legTop, hipZ). */
  legTop: number;
  hipZ: number;
  legX: number;
  /** Above the toes (`toeTop`) the leg is only as wide as its shaft (`legXHi`) — the wing tips hanging beside it stay put. */
  legXHi: number;
  toeTop: number;
  /** Tail feathers resting on the ground behind this z are never leg. */
  tailZ: number;
  /** The folded wings: outside `wingX`, below `shoulderY` (their pivot). */
  wingX: number;
  shoulderY: number;
  /** Everything above this is neck and head (the head bob). */
  neckY: number;
  height: number;
}
export type WildRig =
  | { body: 'quadruped'; rig: WildQuadRig }
  | { body: 'serpent'; rig: SerpentRig }
  | { body: 'drifter'; rig: DrifterRig }
  | { body: 'strider'; rig: StriderRig };

export interface WildParts {
  species: string;
  body: WildBody;
  geometry: THREE.BufferGeometry;
  material: THREE.MeshStandardNodeMaterial;
  rig: WildRig;
  /** Normalised height (fraction of the sizing length). */
  height: number;
  /** A drifter's hover (fraction of the sizing length) — the plate sits above it. */
  hover: number;
}

/** The drifter hovers this high (fraction of its length) and bobs this much; the strider's wings flare / beat this wide. */
export const WILD_SHAPE = { hover: 0.2, bob: 0.03, flare: 0.75, flap: 0.85 } as const;

type Pos = { count: number; getX(i: number): number; getY(i: number): number; getZ(i: number): number };
const posOf = (geo: THREE.BufferGeometry): Pos => geo.getAttribute('position') as unknown as Pos;
const quant = (a: number[], t: number): number => (a.length ? a[Math.min(a.length - 1, Math.max(0, Math.round(t * (a.length - 1))))] : NaN);

/** Height of the frame (the highest vertex). */
function heightOf(p: Pos): number { let H = 0; for (let i = 0; i < p.count; i++) H = Math.max(H, p.getY(i)); return H; }

/** Per-slice x-extent (q03..q97) of the vertices in each `dy` band, optionally filtered. */
function sliceWidths(p: Pos, H: number, dy: number, keep?: (i: number) => boolean): number[] {
  const n = Math.max(1, Math.ceil(H / dy));
  const xs: number[][] = Array.from({ length: n }, () => []);
  for (let i = 0; i < p.count; i++) {
    if (keep && !keep(i)) continue;
    const k = Math.min(n - 1, Math.max(0, Math.floor(p.getY(i) / dy)));
    xs[k].push(p.getX(i));
  }
  return xs.map((a) => { if (a.length < 4) return 0; a.sort((u, v) => u - v); return quant(a, 0.97) - quant(a, 0.03); });
}

/** The quadruped rig + the tail (the stalker's tail tip rests on the ground: feet are looked for within |z| ≤ 0.4). */
export function wildQuadRig(geo: THREE.BufferGeometry, grazeNeck = 0.6): WildQuadRig {
  const r = quadrupedRig(geo, grazeNeck, { footZMax: 0.4 });
  const p = posOf(geo);
  let minZ = 0;
  for (let i = 0; i < p.count; i++) minZ = Math.min(minZ, p.getZ(i));
  return { ...r, tailZ: r.backHipZ - r.legHalf - 0.04, tailEnd: minZ };
}

/** The coiled serpent: the coil is the wide band on the ground; the neck starts where the body narrows. */
export function serpentRig(geo: THREE.BufferGeometry): SerpentRig {
  const p = posOf(geo);
  const H = heightOf(p), dy = 0.02;
  const w = sliceWidths(p, H, dy);
  const maxW = Math.max(...w);
  let coilTop = 0.3 * H;
  for (let k = Math.ceil(0.06 / dy); k < w.length; k++) if (w[k] > 0 && w[k] < 0.45 * maxW) { coilTop = k * dy; break; }
  let sx = 0, sz = 0, n = 0;
  for (let i = 0; i < p.count; i++) if (p.getY(i) < coilTop) { sx += p.getX(i); sz += p.getZ(i); n++; }
  return { coilTop, cx: n ? sx / n : 0, cz: n ? sz / n : 0, height: H };
}

/** The drifter: down from its widest slice, the cap ends where the body narrows to the tentacle bundle. */
export function drifterRig(geo: THREE.BufferGeometry): DrifterRig {
  const p = posOf(geo);
  const H = heightOf(p), dy = 0.02;
  const w = sliceWidths(p, H, dy);
  let kMax = 0;
  for (let k = 0; k < w.length; k++) if (w[k] > w[kMax]) kMax = k;
  let capBottom = 0.5 * H;
  for (let k = kMax; k >= 0; k--) if (w[k] < 0.6 * w[kMax]) { capBottom = (k + 1) * dy; break; }
  let sx = 0, sz = 0, n = 0;
  for (let i = 0; i < p.count; i++) if (p.getY(i) >= capBottom) { sx += p.getX(i); sz += p.getZ(i); n++; }
  return { capBottom, capMid: (capBottom + H) / 2, cx: n ? sx / n : 0, cz: n ? sz / n : 0, height: H };
}

/**
 * The upright bird: the toes are the low band (the tail feathers resting behind them are split off at the widest gap in
 * z); the leg shafts are the thin stretch above the toes, and the body begins where that column thickens again; the
 * wings are the outer plates past the valley of |x|; the shoulders are their top.
 */
export function striderRig(geo: THREE.BufferGeometry): StriderRig {
  const p = posOf(geo);
  const H = heightOf(p);
  // the low band, split into toes and tail at the widest gap in z
  const low: number[] = [];
  for (let i = 0; i < p.count; i++) if (p.getY(i) < 0.1 * H) low.push(p.getZ(i));
  low.sort((a, b) => a - b);
  let tailZ = -Infinity, gap = 0;
  for (let k = 1; k < low.length; k++) if (low[k] - low[k - 1] > gap) { gap = low[k] - low[k - 1]; tailZ = (low[k] + low[k - 1]) / 2; }
  if (gap < 0.08 || tailZ > 0) tailZ = -Infinity; // no separate tail resting on the ground (a gap ahead of the middle is the toes' own)
  const feetX: number[] = [], feetZ: number[] = [];
  for (let i = 0; i < p.count; i++) {
    if (p.getY(i) >= 0.1 * H || p.getZ(i) <= tailZ) continue;
    feetX.push(Math.abs(p.getX(i))); feetZ.push(p.getZ(i));
  }
  feetX.sort((a, b) => a - b); feetZ.sort((a, b) => a - b);
  const legX = feetX.length ? quant(feetX, 0.95) + 0.04 : 0.15;
  const fz0 = feetZ.length ? quant(feetZ, 0.02) - 0.05 : -0.2, fz1 = feetZ.length ? quant(feetZ, 0.98) + 0.05 : 0.2;
  // the leg column, counted per slice: toes (dense) → shafts (thin) → the body (dense again)
  const dy = 0.02, nS = Math.ceil((0.6 * H) / dy);
  const cnt = new Array(nS).fill(0);
  for (let i = 0; i < p.count; i++) {
    const y = p.getY(i), z = p.getZ(i);
    if (y >= 0.6 * H || Math.abs(p.getX(i)) >= legX || z < fz0 || z > fz1 || z <= tailZ) continue;
    cnt[Math.min(nS - 1, Math.floor(y / dy))]++;
  }
  // a 3-slice moving sum: a sparse stretch of a mesh leaves single empty slices that are not the shafts' thinness
  const sm = cnt.map((_, k) => (cnt[k - 1] ?? 0) + cnt[k] + (cnt[k + 1] ?? 0));
  cnt.splice(0, cnt.length, ...sm);
  let peak = 0;
  for (let k = 0; k < Math.ceil((0.15 * H) / dy) && k < nS; k++) if (cnt[k] > cnt[peak]) peak = k;
  let kMin = peak + 1, legTop = 0.26 * H;
  for (let k = peak + 1; k < Math.floor((0.5 * H) / dy) && k < nS; k++) if (cnt[k] < cnt[kMin]) kMin = k;
  for (let k = kMin + 1; k < nS; k++) if (cnt[k] > 2.5 * Math.max(cnt[kMin], 8)) { legTop = k * dy; break; }
  // the hip pivot: the upper stretch of the shafts (the toes reach forward of the hip and outnumber the shafts)
  const hipFrom = legTop - 0.4 * Math.max(0, legTop - peak * dy);
  const hipZs: number[] = [], shaftX: number[] = [];
  for (let i = 0; i < p.count; i++) {
    const y = p.getY(i), z = p.getZ(i);
    if (y < hipFrom || y > legTop || Math.abs(p.getX(i)) >= legX || z < fz0 || z > fz1 || z <= tailZ) continue;
    hipZs.push(z); shaftX.push(Math.abs(p.getX(i)));
  }
  hipZs.sort((a, b) => a - b); shaftX.sort((a, b) => a - b);
  const legXHi = shaftX.length ? Math.min(legX, quant(shaftX, 0.95) + 0.03) : legX;
  const hipZ = hipZs.length ? quant(hipZs, 0.5) : 0;
  // the wings: the valley of |x| between the torso and the outer plates, in the band above the legs
  const band: number[] = [];
  let ax = 0;
  for (let i = 0; i < p.count; i++) { const y = p.getY(i); if (y > legTop + 0.03 && y < 0.6 * H) { const a = Math.abs(p.getX(i)); band.push(a); ax = Math.max(ax, a); } }
  const nb = 20, hist = new Array(nb).fill(0);
  for (const a of band) hist[Math.min(nb - 1, Math.floor((a / Math.max(1e-6, ax)) * nb))]++;
  const k0 = Math.round(0.4 * nb), k1 = Math.round(0.85 * nb);
  let lo = Infinity;
  for (let k = k0; k <= k1; k++) lo = Math.min(lo, hist[k]);
  // the valley: the middle of the longest run of minimal bins
  let best = k0, bestLen = 0;
  for (let k = k0; k <= k1; k++) {
    if (hist[k] !== lo) continue;
    let e = k; while (e + 1 <= k1 && hist[e + 1] === lo) e++;
    if (e - k + 1 > bestLen) { bestLen = e - k + 1; best = (k + e) / 2; }
    k = e;
  }
  const wingX = ((best + 0.5) / nb) * ax;
  const wingY: number[] = [];
  for (let i = 0; i < p.count; i++) if (Math.abs(p.getX(i)) > wingX + 0.03) wingY.push(p.getY(i));
  wingY.sort((a, b) => a - b);
  const shoulderY = wingY.length ? quant(wingY, 0.98) : 0.6 * H;
  return { legTop, hipZ, legX, legXHi, toeTop: 0.1 * H, tailZ: Number.isFinite(tailZ) ? tailZ : -10, wingX, shoulderY, neckY: shoulderY + 0.06, height: H };
}

/** Measure the body's rig. */
export function measureWildRig(body: WildBody, geo: THREE.BufferGeometry): WildRig {
  switch (body) {
    case 'serpent': return { body, rig: serpentRig(geo) };
    case 'drifter': return { body, rig: drifterRig(geo) };
    case 'strider': return { body, rig: striderRig(geo) };
    default: return { body: 'quadruped', rig: wildQuadRig(geo) };
  }
}

type V3 = ReturnType<typeof vec3>;
type V4 = ReturnType<typeof vec4>;
type Nd = ReturnType<typeof float>;

/** The model-space delta of a vertex at a phase (every phase multiplier an integer — lib/wild_gait PHASE_WRAP). */
function wildDelta(w: WildRig, g: V3, A: V4, B: V4, phase: Nd): V3 {
  const swing = A.w, windup = B.x, lunge = B.y, run = B.z;
  if (w.body === 'quadruped') {
    const r = w.rig;
    const legs = deltaFor('quadruped', r, g, A, phase); // the herds' walk and neck, unchanged
    const span = Math.max(0.05, r.tailZ - r.tailEnd);
    const tw = clamp(float(r.tailZ).sub(g.z).div(span), 0, 1);
    const wag = sin(phase.sub(tw.mul(3))).mul(tw.mul(tw)).mul(float(0.05).add(swing.mul(0.08)));
    return legs.add(vec3(wag, 0, 0)) as unknown as V3;
  }
  if (w.body === 'serpent') {
    const r = w.rig;
    const coilW = float(1).sub(smoothstep(r.coilTop - 0.06, r.coilTop + 0.02, g.y));
    const ox = g.x.sub(r.cx), oz = g.z.sub(r.cz);
    const rad = max(length(vec2(ox, oz)), 0.02);
    const th = atan(ox, oz);
    const wave = th.mul(3).sub(phase);
    const dr = sin(wave).mul(float(0.008).add(swing.mul(0.026))).mul(coilW);
    const heave = max(sin(wave.add(1.3)), 0).mul(float(0.006).add(swing.mul(0.02))).mul(coilW).mul(clamp(g.y.div(Math.max(0.05, r.coilTop)), 0, 1));
    const neckW = smoothstep(r.coilTop - 0.02, r.coilTop + 0.08, g.y);
    const s = clamp(g.y.sub(r.coilTop).div(Math.max(0.05, r.height - r.coilTop)), 0, 1);
    const sway = sin(phase.sub(s.mul(4.5))).mul(float(0.025).add(swing.mul(0.035))).mul(s).mul(neckW);
    const s2 = s.mul(s).mul(neckW);
    const dz = s2.mul(lunge.mul(0.22).sub(windup.mul(0.16)));
    const dy = s2.mul(windup.mul(0.05).sub(lunge.mul(0.1)));
    return vec3(ox.div(rad).mul(dr).add(sway), heave.add(dy), oz.div(rad).mul(dr).add(dz)) as unknown as V3;
  }
  if (w.body === 'drifter') {
    const r = w.rig;
    const capW = smoothstep(r.capBottom - 0.05, r.capBottom + 0.05, g.y);
    const tentW = float(1).sub(capW);
    const lift = float(WILD_SHAPE.hover).add(sin(phase).mul(WILD_SHAPE.bob)).add(windup.mul(0.06)).sub(lunge.mul(0.05));
    const pulse = sin(phase).mul(0.05).sub(windup.mul(0.07)).add(lunge.mul(0.06)).mul(capW);
    const depth = clamp(float(r.capBottom).sub(g.y).div(Math.max(0.05, r.capBottom)), 0, 1).mul(tentW);
    const sd = g.x.add(g.z).mul(13);
    const tx = depth.mul(float(0.035).add(swing.mul(0.03))).mul(sin(phase.sub(depth.mul(5)).add(sd)));
    const tz = depth.mul(cos(phase.mul(2).sub(depth.mul(4)).add(sd.mul(0.7))).mul(0.03).sub(run.mul(depth).mul(0.1)));
    return vec3(
      g.x.sub(r.cx).mul(pulse).add(tx),
      lift.sub(g.y.sub(r.capMid).mul(pulse).mul(0.6)),
      g.z.sub(r.cz).mul(pulse).add(tz),
    ) as unknown as V3;
  }
  // strider
  const r = w.rig;
  const legXy = float(r.legX).add(float(r.legXHi - r.legX).mul(smoothstep(r.toeTop, r.toeTop + 0.04, g.y)));
  const legW = float(1).sub(smoothstep(r.legTop - 0.04, r.legTop, g.y))
    .mul(float(1).sub(smoothstep(legXy, legXy.add(0.04), abs(g.x))))
    .mul(smoothstep(r.tailZ - 0.03, r.tailZ + 0.03, g.z));
  const side = step(0, g.x).mul(Math.PI);
  const a = swing.mul(sin(phase.add(side))).mul(legW);
  const ly = g.y.sub(r.legTop), lz = g.z.sub(r.hipZ);
  const ca = cos(a), sa = sin(a);
  const legDy = ly.mul(ca).sub(lz.mul(sa)).add(r.legTop).sub(g.y);
  const legDz = ly.mul(sa).add(lz.mul(ca)).add(r.hipZ).sub(g.z);
  const lowFrac = clamp(float(1).sub(g.y.div(Math.max(0.05, r.legTop))), 0, 1);
  const footLift = max(sin(phase.add(side)), 0).mul(swing).mul(0.06).mul(legW).mul(lowFrac);
  const bob = abs(sin(phase)).mul(swing).mul(0.02).mul(float(1).sub(legW));
  const headW = smoothstep(r.neckY - 0.04, r.neckY + 0.08, g.y);
  const headDz = headW.mul(sin(phase.mul(2)).mul(swing).mul(0.05).sub(windup.mul(0.06)).add(lunge.mul(0.1)));
  const wingW = smoothstep(r.wingX, r.wingX + 0.04, abs(g.x))
    .mul(smoothstep(r.legTop, r.legTop + 0.1, g.y))
    .mul(float(1).sub(smoothstep(r.shoulderY, r.shoulderY + 0.06, g.y)));
  const f = windup.mul(WILD_SHAPE.flare).add(run.mul(WILD_SHAPE.flap).mul(float(0.5).sub(cos(phase.mul(2)).mul(0.5)))).mul(wingW);
  const rx = abs(g.x).sub(r.wingX), ry = g.y.sub(r.shoulderY);
  const cf = cos(f), sf = sin(f);
  const wingDx = sign(g.x).mul(rx.mul(cf).sub(ry.mul(sf)).sub(rx));
  const wingDy = rx.mul(sf).add(ry.mul(cf)).sub(ry);
  return vec3(wingDx, legDy.add(footLift).add(bob).add(wingDy), legDz.add(headDz)) as unknown as V3;
}

type ObjUpd = { onObjectUpdate(cb: (frame: { object?: THREE.Object3D }, self: { value: THREE.Vector4 }) => void): unknown };
/** A vec4 uniform refreshed for EVERY render object from `object.userData[key]` (the object group — re-uploaded per draw). */
function perObject(key: 'wildA' | 'wildB'): V4 {
  const u = uniform(new THREE.Vector4());
  (u as unknown as ObjUpd).onObjectUpdate((frame, self) => {
    const v = frame.object?.userData?.[key] as THREE.Vector4 | undefined;
    if (v) self.value.copy(v);
  });
  return u as unknown as V4;
}

/** One node material per species: the cooked material's maps + the body's vertex animation, read per object. */
export function wildCreatureMaterial(src: THREE.MeshStandardMaterial | null, w: WildRig): THREE.MeshStandardNodeMaterial {
  const mat = new THREE.MeshStandardNodeMaterial();
  mat.name = `wild-${w.body}`;
  if (src) {
    mat.map = src.map ?? null;
    mat.normalMap = src.normalMap ?? null;
    if (src.normalMap && src.normalScale) mat.normalScale = src.normalScale.clone();
    mat.roughnessMap = src.roughnessMap ?? null; mat.metalnessMap = src.metalnessMap ?? null;
    mat.roughness = src.roughness ?? 0.85; mat.metalness = src.metalness ?? 0;
    mat.emissiveMap = src.emissiveMap ?? null;
    if (src.emissive) mat.emissive.copy(src.emissive);
    if (!src.map && src.color) mat.color.copy(src.color);
  }
  mat.side = src?.side ?? THREE.FrontSide;
  // per DRAW, never per material: each beast's own numbers (object-group uniforms, refreshed for every render object)
  const A = perObject('wildA');
  const B = perObject('wildB');
  // r185/WebGPU LAW: an assign() needs an enclosing Fn() stack
  mat.positionNode = Fn(() => {
    const g = positionGeometry as unknown as V3;
    positionPrevious.assign(positionLocal.add(wildDelta(w, g, A, B, A.y as unknown as Nd)));
    return positionLocal.add(wildDelta(w, g, A, B, A.x as unknown as Nd));
  })();
  return mat;
}

/** The first mesh of a loaded representation, its world matrix current. */
function firstMesh(object: THREE.Object3D): THREE.Mesh | null {
  object.updateMatrixWorld(true);
  let m: THREE.Mesh | null = null;
  object.traverse((o) => { if (!m && (o as THREE.Mesh).isMesh) m = o as THREE.Mesh; });
  return m;
}

/**
 * Build a species' drawable parts from its loaded master (once per species per session — WildsModule caches them and
 * disposes them on a flush). The geometry and material are the caller's own (the master is the asset cache's).
 */
export function wildParts(object: THREE.Object3D, species: string): WildParts | null {
  const mesh = firstMesh(object);
  if (!mesh) return null;
  const spec = wildGaitFor(species);
  const kind = spec.body === 'quadruped' ? 'quadruped' : 'wings';
  const { geo, height } = normalizeCreature(mesh.geometry, mesh.matrixWorld, kind, false, { unit: 'xz', ground: true });
  const rig = measureWildRig(spec.body, geo);
  // the bend reaches past the rest bounds (a drifter's hover, a flared wing): cull by a looser sphere
  if (geo.boundingSphere) geo.boundingSphere.radius *= 1.35;
  const src = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as THREE.MeshStandardMaterial;
  return {
    species, body: spec.body, geometry: geo, material: wildCreatureMaterial(src ?? null, rig), rig, height,
    hover: spec.body === 'drifter' ? WILD_SHAPE.hover + WILD_SHAPE.bob : 0,
  };
}
